import React, { useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { FaSearch, FaPrint, FaArrowLeft, FaInfoCircle } from "react-icons/fa";

const API_BASE = "http://nissi-india.com/api/vehiclehsdFillingRpt1";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

const isSelect = (v) => {
  const s = String(v ?? "").trim().toLowerCase();
  return !s || s === "select" || s === "selec";
};

const pad2 = (n) => String(n).padStart(2, "0");

const fmtDate = (isoOrDate) => {
  if (!isoOrDate) return "";
  if (typeof isoOrDate === "string" && isoOrDate.includes("/")) return isoOrDate;
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return String(isoOrDate);
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const fmtNum2 = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

function normalizeRow(x, idx) {
  return {
    sno: idx + 1,
    fld_vehicle_code: x?.fld_vehicle_code ?? x?.vehicleCode ?? "",
    fld_vehicle_name: x?.fld_vehicle_name ?? x?.vehicleName ?? "",
    fld_vehicle_regno: x?.fld_vehicle_regno ?? x?.regNo ?? "",
    fld_hsdfillingWorkinhr: fmtNum2(x?.fld_hsdfillingWorkinhr ?? x?.workHr),
    fld_hsdfillinghr: fmtNum2(x?.fld_hsdfillinghr ?? x?.hsdHr),
    fld_hsdfillingWorkingKM: fmtNum2(x?.fld_hsdfillingWorkingKM ?? x?.workKm),
    fld_hsdfillingkm: fmtNum2(x?.fld_hsdfillingkm ?? x?.hsdKm),
    fld_diselconsumptionhr: fmtNum2(x?.fld_diselconsumptionhr ?? x?.consHr),
    fld_diselconsumptionkm: fmtNum2(x?.fld_diselconsumptionkm ?? x?.consKm),
    cumulativehrs: fmtNum2(x?.cumulativehrs ?? x?.cumHr),
    cumulativekm: fmtNum2(x?.cumulativekm ?? x?.cumKm),
    fld_progressentrydate: fmtDate(x?.fld_progressentrydate ?? x?.lastUpdatedDate),
  };
}

export default function VehicleHsdFillingReport1() {
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const skin = useMemo(() => getCurrentSkin(theme), [theme]);

  /* ---------------- localStorage session values ---------------- */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const sessionBranchId = localStorage.getItem("branchId") || "";
  const sessionBranchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": sessionBranchId,
      "x-branchtype": sessionBranchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, sessionBranchType, userId]
  );

  /* ---------------- state: filters ---------------- */
  const [vehicleMode, setVehicleMode] = useState("O"); // O own, H hire
  const [branches, setBranches] = useState([{ label: "Select", value: "" }]);
  const [branch, setBranch] = useState("");

  const [vehicleSubTypes, setVehicleSubTypes] = useState([{ label: "Select", value: "" }]);
  const [vehicleSubType, setVehicleSubType] = useState("");

  const [vehicleCode, setVehicleCode] = useState("");
  const [vehicleName, setVehicleName] = useState("");
  const [regNo, setRegNo] = useState("");

  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  /* ---------------- autocomplete ---------------- */
  const [codeSug, setCodeSug] = useState([]);
  const [nameSug, setNameSug] = useState([]);
  const [regSug, setRegSug] = useState([]);

  /* ---------------- grid ---------------- */
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const initLoadedRef = useRef(false);

  /* ---------------- init ---------------- */
  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);

      const ownHire = vehicleMode === "H" ? "HIRE" : "OWN";

      const res = await axios.get(`${API_BASE}/init`, {
        headers: apiHeaders,
        params: { ownHire },
      });

      const data = res?.data || {};
      const b = Array.isArray(data?.branches) ? data.branches : [];
      const vst = Array.isArray(data?.vehicleSubTypes) ? data.vehicleSubTypes : [];
      const defaults = data?.defaults || {};

      setBranches(b.length ? b : [{ label: "Select", value: "" }]);
      setVehicleSubTypes(vst.length ? vst : [{ label: "Select", value: "" }]);

      setBranch(toStr(defaults?.branchId));
      setFromDate(toStr(defaults?.fromDate));
      setToDate(toStr(defaults?.toDate));
      setMonth(toStr(defaults?.month));
      setYear(toStr(defaults?.year));
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed to load initial data");
    } finally {
      setPageLoading(false);
    }
  }, [apiHeaders, vehicleMode]);

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  /* ---------------- initial search once ---------------- */
  useEffect(() => {
    if (pageLoading) return;
    if (initLoadedRef.current) return;
    initLoadedRef.current = true;
    handleSearch(true);
  }, [pageLoading]);

  /* ---------------- reload init on Own/Hire change ---------------- */
  useEffect(() => {
    if (!initLoadedRef.current) return;
    loadInit();
  }, [vehicleMode, loadInit]);

  /* ---------------- autocomplete fetcher ---------------- */
  const fetchAuto = useCallback(
    async (type, text) => {
      const q = toStr(text);
      if (!q) return [];

      const url =
        type === "code"
          ? `${API_BASE}/ac/vehicleCode`
          : type === "name"
          ? `${API_BASE}/ac/vehicleName`
          : `${API_BASE}/ac/vehicleRegno`;

      const ownHire = vehicleMode === "H" ? "HIRE" : "OWN";

      const res = await axios.get(url, {
        headers: apiHeaders,
        params: {
          q,
          ownHire,
          branchId: branch || sessionBranchId || "",
          branchType: sessionBranchType || "HO",
          userId,
          vehicletype1: "%",
        },
      });

      return Array.isArray(res?.data?.items) ? res.data.items : [];
    },
    [apiHeaders, branch, sessionBranchId, sessionBranchType, userId, vehicleMode]
  );

  const onTypeVehicleCode = async (v) => {
    setVehicleCode(v);
    try {
      const items = await fetchAuto("code", v);
      setCodeSug(items);
    } catch {
      setCodeSug([]);
    }
  };

  const onTypeVehicleName = async (v) => {
    setVehicleName(v);
    try {
      const items = await fetchAuto("name", v);
      setNameSug(items);
    } catch {
      setNameSug([]);
    }
  };

  const onTypeRegNo = async (v) => {
    setRegNo(v);
    try {
      const items = await fetchAuto("regno", v);
      setRegSug(items);
    } catch {
      setRegSug([]);
    }
  };

  /* ---------------- build params ---------------- */
  const buildListParams = useCallback(() => {
    const ownHire = vehicleMode === "H" ? "HIRE" : "OWN";

    return {
      ownHire,
      branchId: isSelect(branch) ? "" : branch,
      branch: isSelect(branch) ? "Select" : branch,
      vehicleCode: vehicleCode || "",
      vehicleName: vehicleName || "",
      vehicleRegno: regNo || "",
      fromDate: fromDate || "",
      toDate: toDate || "",
      month: month || "Select",
      year: year || "Select",
      vehicleSubTypeIndex: vehicleSubType || "Select",
      branchType: sessionBranchType || "HO",
      sessionBranchId: sessionBranchId || "",
    };
  }, [
    vehicleMode,
    branch,
    vehicleCode,
    vehicleName,
    regNo,
    fromDate,
    toDate,
    month,
    year,
    vehicleSubType,
    sessionBranchType,
    sessionBranchId,
  ]);

  /* ---------------- actions ---------------- */
  const handleSearch = async (silent = false) => {
    setLoading(true);
    try {
      const params = buildListParams();

      const res = await axios.get(`${API_BASE}/list`, {
        headers: apiHeaders,
        params,
      });

      if (res?.data?.success === false) {
        toast.error(res?.data?.message || "Search failed");
        setRows([]);
        return;
      }

      const list = Array.isArray(res?.data?.rows) ? res.data.rows : [];
      setRows(list);

      if (!silent) {
        toast.success(`Loaded ${list.length} row(s)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Search failed");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    try {
      const params = buildListParams();

      const res = await axios.get(`${API_BASE}/printUrl`, {
        headers: apiHeaders,
        params,
      });

      if (res?.data?.success === false) {
        toast.error(res?.data?.message || "Print URL generation failed");
        return;
      }

      const url = res?.data?.url || res?.data?.legacyUrl;
      if (!url) {
        toast.error("Print URL not available");
        return;
      }

      window.open(url, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Print failed");
    }
  };

  const openVehicleDetail = async (vehCode) => {
    try {
      const res = await axios.get(`${API_BASE}/openVehicleHsdReport`, {
        headers: apiHeaders,
        params: {
          vehicleCode: vehCode,
          month: month || "Select",
          year: year || "Select",
          fromDate: fromDate || "",
          toDate: toDate || "",
        },
      });

      if (res?.data?.success === false) {
        toast.error(res?.data?.message || "Open detail failed");
        return;
      }

      const url = res?.data?.url || res?.data?.legacyUrl;
      if (!url) {
        toast.error("Detail URL not available");
        return;
      }

      navigate(url);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Open detail failed");
    }
  };

  const normalized = useMemo(() => rows.map(normalizeRow), [rows]);

  return (
    <>
      <HeaderBar />

      <VehicleLayoutWrapper>
        <ToastContainer />

        <div className="p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(-1)}
                className="px-3 py-2 border rounded hover:bg-gray-50"
                title="Back"
              >
                <FaArrowLeft />
              </button>

              <div>
                <div
                  className="text-lg font-bold"
                  style={{ color: skin?.textColor || "#111" }}
                >
                  Vehicle HSD Filling HR/KM Report
                </div>
                <div className="text-xs text-gray-600 flex items-center gap-2">
                  <FaInfoCircle /> Old ASPX: VehiclehsdfillingWholeReport.aspx
                </div>
              </div>
            </div>

            <div className="text-xs text-gray-600">
              Mode: {vehicleMode === "H" ? "Hire" : "Own"}
            </div>
          </div>

          <div className="border rounded p-3 bg-white shadow-sm">
            <div className="flex items-center gap-6 mb-3">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="vehicleMode"
                  checked={vehicleMode === "O"}
                  onChange={() => setVehicleMode("O")}
                />
                <span>Own Vehicle</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="vehicleMode"
                  checked={vehicleMode === "H"}
                  onChange={() => setVehicleMode("H")}
                />
                <span>Hire Vehicle</span>
              </label>
            </div>

            <div className="grid grid-cols-12 gap-3 mb-3">
              <div className="col-span-12 md:col-span-2 text-sm font-semibold">
                Branch
              </div>
              <div className="col-span-12 md:col-span-4">
                <select
                  className="w-full border rounded px-2 py-2"
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                  disabled={(sessionBranchType || "").toUpperCase() !== "HO"}
                >
                  {branches.map((b, i) => (
                    <option key={`${b.value}-${i}`} value={b.value}>
                      {b.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-span-12 md:col-span-2 text-sm font-semibold">
                Vehicle Code
              </div>
              <div className="col-span-12 md:col-span-4">
                <input
                  className="w-full border rounded px-2 py-2"
                  value={vehicleCode}
                  onChange={(e) => onTypeVehicleCode(e.target.value)}
                  list="vehCodeSug"
                />
                <datalist id="vehCodeSug">
                  {codeSug.map((x, i) => (
                    <option key={i} value={x} />
                  ))}
                </datalist>
              </div>
            </div>

            <div className="grid grid-cols-12 gap-3 mb-3">
              <div className="col-span-12 md:col-span-2 text-sm font-semibold">
                Vehicle RegistrationNumber
              </div>
              <div className="col-span-12 md:col-span-4">
                <input
                  className="w-full border rounded px-2 py-2"
                  value={regNo}
                  onChange={(e) => onTypeRegNo(e.target.value)}
                  list="vehRegSug"
                />
                <datalist id="vehRegSug">
                  {regSug.map((x, i) => (
                    <option key={i} value={x} />
                  ))}
                </datalist>
              </div>

              <div className="col-span-12 md:col-span-2 text-sm font-semibold">
                Vehicle Name
              </div>
              <div className="col-span-12 md:col-span-4">
                <input
                  className="w-full border rounded px-2 py-2"
                  value={vehicleName}
                  onChange={(e) => onTypeVehicleName(e.target.value)}
                  list="vehNameSug"
                />
                <datalist id="vehNameSug">
                  {nameSug.map((x, i) => (
                    <option key={i} value={x} />
                  ))}
                </datalist>
              </div>
            </div>

            <div className="grid grid-cols-12 gap-3 mb-3">
              <div className="col-span-12 md:col-span-2 text-sm font-semibold">
                Vehicle Sub Type
              </div>
              <div className="col-span-12 md:col-span-4">
                <select
                  className="w-full border rounded px-2 py-2"
                  value={vehicleSubType}
                  onChange={(e) => setVehicleSubType(e.target.value)}
                >
                  {vehicleSubTypes.map((v, i) => (
                    <option key={`${v.value}-${i}`} value={v.value}>
                      {v.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-span-12 md:col-span-2 text-sm font-semibold">
                Month
              </div>
              <div className="col-span-12 md:col-span-4">
                <select
                  className="w-full border rounded px-2 py-2"
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                >
                  {[
                    { label: "Select", value: "" },
                    { label: "January", value: "January" },
                    { label: "February", value: "February" },
                    { label: "March", value: "March" },
                    { label: "April", value: "April" },
                    { label: "May", value: "May" },
                    { label: "June", value: "June" },
                    { label: "July", value: "July" },
                    { label: "August", value: "August" },
                    { label: "September", value: "September" },
                    { label: "October", value: "October" },
                    { label: "November", value: "November" },
                    { label: "December", value: "December" },
                  ].map((m) => (
                    <option key={m.label} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-12 gap-3 mb-3">
              <div className="col-span-12 md:col-span-2 text-sm font-semibold">
                Year
              </div>
              <div className="col-span-12 md:col-span-4">
                <select
                  className="w-full border rounded px-2 py-2"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                >
                  <option value="">Select</option>
                  {Array.from({ length: new Date().getFullYear() + 1 - 2006 + 1 }).map(
                    (_, i) => {
                      const y = String(2006 + i);
                      return (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      );
                    }
                  )}
                </select>
              </div>

              <div className="col-span-12 md:col-span-2 text-sm font-semibold">
                From Date
              </div>
              <div className="col-span-12 md:col-span-4">
                <input
                  className="w-full border rounded px-2 py-2"
                  placeholder="dd/MM/yyyy"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-12 gap-3 mb-3">
              <div className="col-span-12 md:col-span-2 text-sm font-semibold">
                To Date
              </div>
              <div className="col-span-12 md:col-span-4">
                <input
                  className="w-full border rounded px-2 py-2"
                  placeholder="dd/MM/yyyy"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
            </div>

            <div className="flex items-center justify-center gap-3 mt-2">
              <button
                onClick={() => handleSearch(false)}
                className="px-4 py-2 rounded text-white font-semibold flex items-center gap-2"
                style={{ background: skin?.primaryColor || "#2563eb" }}
                disabled={loading || pageLoading}
              >
                <FaSearch />
                {loading ? "Searching..." : "Search"}
              </button>

              <button
                onClick={handlePrint}
                className="px-4 py-2 rounded border font-semibold flex items-center gap-2 hover:bg-gray-50"
                title="Print"
                disabled={loading || pageLoading}
              >
                <FaPrint /> Print
              </button>
            </div>
          </div>

          <div className="mt-4 border rounded bg-white shadow-sm p-2 overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border px-2 py-2 text-center">S.No</th>
                  <th className="border px-2 py-2 text-center">Open</th>
                  <th className="border px-2 py-2 text-center">Vehicle Code</th>
                  <th className="border px-2 py-2 text-center">Vehicle Name</th>
                  <th className="border px-2 py-2 text-center">Vehicle Regno</th>
                  <th className="border px-2 py-2 text-center">Total Working Hr</th>
                  <th className="border px-2 py-2 text-center">Total HSD Filling (Hr)</th>
                  <th className="border px-2 py-2 text-center">Total Working KM</th>
                  <th className="border px-2 py-2 text-center">Total HSD Filling (KM)</th>
                  <th className="border px-2 py-2 text-center">HSD Consump Hr</th>
                  <th className="border px-2 py-2 text-center">HSD Consump KM</th>
                  <th className="border px-2 py-2 text-center">Last Cum Hr</th>
                  <th className="border px-2 py-2 text-center">Last Cum KM</th>
                  <th className="border px-2 py-2 text-center">Last Updated Date</th>
                </tr>
              </thead>

              <tbody>
                {!normalized.length ? (
                  <tr>
                    <td colSpan={14} className="border px-2 py-3 text-center text-gray-600">
                      {pageLoading ? "Loading..." : "No Records Found"}
                    </td>
                  </tr>
                ) : (
                  normalized.map((r) => (
                    <tr key={`${r.fld_vehicle_code}-${r.sno}`}>
                      <td className="border px-2 py-2 text-center">{r.sno}</td>

                      <td className="border px-2 py-2 text-center">
                        <button
                          onClick={() => openVehicleDetail(r.fld_vehicle_code)}
                          className="text-blue-700 underline font-semibold"
                          title="Open vehicle HSD filling detail"
                        >
                          Open
                        </button>
                      </td>

                      <td className="border px-2 py-2 text-center">{r.fld_vehicle_code}</td>
                      <td className="border px-2 py-2">{r.fld_vehicle_name}</td>
                      <td className="border px-2 py-2 text-center">{r.fld_vehicle_regno}</td>
                      <td className="border px-2 py-2 text-right">{r.fld_hsdfillingWorkinhr}</td>
                      <td className="border px-2 py-2 text-right">{r.fld_hsdfillinghr}</td>
                      <td className="border px-2 py-2 text-right">{r.fld_hsdfillingWorkingKM}</td>
                      <td className="border px-2 py-2 text-right">{r.fld_hsdfillingkm}</td>
                      <td className="border px-2 py-2 text-right">{r.fld_diselconsumptionhr}</td>
                      <td className="border px-2 py-2 text-right">{r.fld_diselconsumptionkm}</td>
                      <td className="border px-2 py-2 text-right">{r.cumulativehrs}</td>
                      <td className="border px-2 py-2 text-right">{r.cumulativekm}</td>
                      <td className="border px-2 py-2 text-center">{r.fld_progressentrydate}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </VehicleLayoutWrapper>
    </>
  );
}